import { useCallback, useEffect, useState } from "react";
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  FileUp,
  Loader2,
  Search,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useFarmaciaAuth } from "../../context/FarmaciaAuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { farmGet, farmPost } from "./shared";
import { FarmaciaPageChrome } from "../../components/FarmaciaPageChrome";

const FIELD_LABELS = {
  codice: "Codice interno *",
  minsan: "MINSAN",
  aic: "AIC",
  nome: "Nome",
  principio_attivo: "Principio attivo",
  dosaggio: "Dosaggio",
  formato: "Formato",
  produttore: "Produttore",
  prezzo: "Prezzo",
  iva: "IVA",
  stato: "Stato",
  regime_distributivo: "Regime distributivo",
  regione: "Regione",
  piano_terapeutico_richiesto: "Piano terapeutico richiesto",
};

export default function CatalogoFarmacia() {
  const { token } = useFarmaciaAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [q, setQ] = useState("");
  const [qApplied, setQApplied] = useState("");
  const [loading, setLoading] = useState(true);
  const [importazioni, setImportazioni] = useState([]);
  const [step, setStep] = useState("list"); // list | map | preview | done
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [catalogo, hist] = await Promise.all([
        farmGet("/farmacia/prodotti", token, {
          page,
          limit: 50,
          ...(qApplied ? { q: qApplied } : {}),
        }),
        farmGet("/farmacia/prodotti/importazioni", token, { limit: 10 }).catch(() => ({ items: [] })),
      ]);
      setItems(catalogo.items || []);
      setTotal(catalogo.total || 0);
      setPages(catalogo.pages || 1);
      setImportazioni(hist.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento catalogo");
    } finally {
      setLoading(false);
    }
  }, [token, page, qApplied]);

  useEffect(() => {
    load();
  }, [load]);

  const applySearch = () => {
    setPage(1);
    setQApplied(q.trim());
  };

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await farmPost("/farmacia/prodotti/import/preview", token, fd, true);
      setPreview(res);
      setMapping(res.mapping || {});
      setStep("map");
      toast.message(`File letto: ${res.total_rows || 0} righe`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anteprima fallita");
    } finally {
      setBusy(false);
    }
  };

  const refreshPreviewWithMapping = async () => {
    if (!preview?.preview_id) return;
    if (!mapping.codice) {
      toast.error("Associa la colonna Codice interno");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("preview_id", preview.preview_id);
      fd.append("mapping_json", JSON.stringify(mapping));
      const res = await farmPost("/farmacia/prodotti/import/remap", token, fd, true);
      setPreview(res);
      setMapping(res.mapping || mapping);
      setStep("preview");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aggiornamento anteprima fallito");
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!preview?.preview_id) return;
    if (!mapping.codice) {
      toast.error("Associa la colonna Codice interno");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("preview_id", preview.preview_id);
      fd.append("mapping_json", JSON.stringify(mapping));
      const res = await farmPost("/farmacia/prodotti/import/commit", token, fd, true);
      setResult(res);
      setStep("done");
      toast.success(`Import: ${res.nuovi} nuovi, ${res.aggiornati} aggiornati`);
      setPage(1);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import fallito");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <FarmaciaPageChrome>
        {step === "list" && (
          <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-xl bg-[#4CAF50] px-3 text-sm font-medium text-white hover:bg-[#43A047]">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
            Importa Excel/CSV
            <input
              type="file"
              accept=".csv,.xlsx,.xlsm"
              className="hidden"
              disabled={busy}
              onChange={(e) => {
                onFile(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </label>
        )}
      </FarmaciaPageChrome>

      {step === "list" && (
        <>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                className="h-11 rounded-xl pl-9"
                placeholder="Cerca nome, codice, MINSAN, AIC, principio…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applySearch()}
              />
            </div>
            <Button variant="outline" className="h-11 rounded-xl" onClick={applySearch}>
              Cerca
            </Button>
          </div>

          <Card className="overflow-hidden rounded-xl shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between border-b px-4 py-3">
              <CardTitle className="flex items-center gap-2 text-sm">
                <BookOpen className="h-4 w-4 text-emerald-600" />
                Prodotti ({total})
              </CardTitle>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span>
                  {page} / {pages}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={page >= pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              {loading ? (
                <div className="flex justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                </div>
              ) : items.length === 0 ? (
                <p className="py-12 text-center text-sm text-slate-400">
                  Catalogo vuoto. Importa un file Excel o CSV per iniziare.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50/60 text-left text-xs uppercase text-slate-500">
                      <th className="px-3 py-2">Codice</th>
                      <th className="px-3 py-2">Nome</th>
                      <th className="px-3 py-2">EAN</th>
                      <th className="px-3 py-2">MINSAN</th>
                      <th className="px-3 py-2">Forma</th>
                      <th className="px-3 py-2">Principio</th>
                      <th className="px-3 py-2">Prezzo</th>
                      <th className="px-3 py-2">Fonte</th>
                      <th className="px-3 py-2">Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((p) => (
                      <tr key={p.id || p.codice} className="border-b border-slate-50 hover:bg-slate-50/40">
                        <td className="px-3 py-2 font-mono text-xs">{p.codice}</td>
                        <td className="px-3 py-2 font-medium">{p.nome}</td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-500">{p.ean || "—"}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{p.minsan || "—"}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{p.forma || p.formato || "—"}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">
                          {p.principio_attivo || "—"}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {p.prezzo != null ? Number(p.prezzo).toFixed(2) : "—"}
                        </td>
                        <td className="px-3 py-2 text-[10px] text-slate-400">
                          {p.fonte || "—"}
                          {p.data_aggiornamento
                            ? ` · ${String(p.data_aggiornamento).slice(0, 10)}`
                            : ""}
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className="text-[10px]">
                            {p.stato || (p.active === false ? "inattivo" : "attivo")}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          {importazioni.length > 0 && (
            <Card className="rounded-xl shadow-sm">
              <CardHeader className="border-b px-4 py-3">
                <CardTitle className="text-sm">Storico importazioni</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 p-4 text-sm">
                {importazioni.map((imp) => (
                  <div
                    key={imp.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2"
                  >
                    <div>
                      <p className="font-medium text-slate-800">{imp.file_name}</p>
                      <p className="text-xs text-slate-500">
                        {(imp.created_at || "").slice(0, 19).replace("T", " ")} · {imp.total_rows}{" "}
                        righe
                      </p>
                    </div>
                    <div className="text-xs text-slate-600">
                      <span className="text-emerald-700">+{imp.nuovi}</span>
                      {" / "}
                      <span className="text-sky-700">↻{imp.aggiornati}</span>
                      {" / "}
                      <span className="text-slate-500">∅{imp.saltati}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {step === "map" && preview && (
        <Card className="rounded-xl shadow-sm">
          <CardHeader className="border-b px-4 py-3">
            <CardTitle className="text-sm">
              Associa colonne — {preview.file_name} ({preview.total_rows} righe)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 p-4">
            <p className="text-xs text-slate-500">
              Il campo <strong>codice</strong> è il codice interno già usato negli ordini: non
              rinominarlo. Mappa almeno codice e preferibilmente nome.
            </p>
            <div className="grid gap-2 md:grid-cols-2">
              {(preview.fields || Object.keys(FIELD_LABELS)).map((field) => (
                <div key={field} className="flex items-center gap-2 text-sm">
                  <label className="w-44 shrink-0 text-xs font-medium text-slate-600">
                    {FIELD_LABELS[field] || field}
                  </label>
                  <select
                    className="h-9 flex-1 rounded-lg border border-slate-200 px-2"
                    value={mapping[field] || ""}
                    onChange={(e) =>
                      setMapping((m) => ({ ...m, [field]: e.target.value || null }))
                    }
                  >
                    <option value="">— non mappare —</option>
                    {(preview.headers || []).map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            {(preview.errors_sample || []).length > 0 && (
              <div className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {(preview.errors_sample || []).slice(0, 5).map((e, i) => (
                  <p key={i}>{e}</p>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" className="rounded-xl" onClick={() => setStep("list")}>
                Annulla
              </Button>
              <Button
                className="rounded-xl bg-[#4CAF50] hover:bg-[#43A047]"
                disabled={!mapping.codice}
                onClick={refreshPreviewWithMapping}
              >
                Continua all&apos;anteprima
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === "preview" && preview && (
        <Card className="rounded-xl shadow-sm">
          <CardHeader className="border-b px-4 py-3">
            <CardTitle className="text-sm">Anteprima e controllo errori</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 p-4">
            <div className="flex flex-wrap gap-3 text-sm">
              <span>
                Righe: <strong>{preview.total_rows}</strong>
              </span>
              <span className="text-rose-700">
                Errori stimati: {preview.error_rows ?? "—"}
              </span>
              <span className="text-amber-700">
                Warning: {preview.warning_rows ?? "—"}
              </span>
              <span>Codici unici: {preview.unique_codici ?? "—"}</span>
            </div>
            <p className="text-xs text-slate-500">
              All&apos;import: prodotti nuovi vengono creati, quelli con stesso{" "}
              <strong>codice</strong> aggiornati. Righe senza codice o con errori gravi sono saltate.
            </p>
            <div className="overflow-x-auto rounded-lg border border-slate-100">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 text-left text-slate-500">
                    <th className="px-2 py-1.5">Riga</th>
                    <th className="px-2 py-1.5">Codice</th>
                    <th className="px-2 py-1.5">Nome</th>
                    <th className="px-2 py-1.5">Esito</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview.sample || []).map((s) => (
                    <tr key={s.row} className="border-t border-slate-50">
                      <td className="px-2 py-1.5">{s.row}</td>
                      <td className="px-2 py-1.5 font-mono">{s.product?.codice || "—"}</td>
                      <td className="px-2 py-1.5">{s.product?.nome || "—"}</td>
                      <td className="px-2 py-1.5">
                        {s.ok ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700">
                            <CheckCircle2 className="h-3 w-3" /> OK
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-rose-700">
                            <AlertTriangle className="h-3 w-3" />
                            {(s.errors || [])[0] || "Errore"}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" className="rounded-xl" onClick={() => setStep("map")}>
                Torna al mapping
              </Button>
              <Button
                className="rounded-xl bg-[#4CAF50] hover:bg-[#43A047]"
                disabled={busy || !mapping.codice}
                onClick={commit}
              >
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Conferma import
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === "done" && result && (
        <Card className="rounded-xl shadow-sm">
          <CardHeader className="border-b px-4 py-3">
            <CardTitle className="text-sm">Import completato</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 p-4 text-sm">
            <p>
              Nuovi: <strong className="text-emerald-700">{result.nuovi}</strong> · Aggiornati:{" "}
              <strong className="text-sky-700">{result.aggiornati}</strong> · Saltati:{" "}
              <strong>{result.saltati}</strong> · Totale file: {result.total_rows}
            </p>
            {(result.errori_sample || []).length > 0 && (
              <div className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {(result.errori_sample || []).slice(0, 8).map((e, i) => (
                  <p key={i}>{e}</p>
                ))}
              </div>
            )}
            <Button
              className="rounded-xl bg-[#4CAF50] hover:bg-[#43A047]"
              onClick={() => {
                setStep("list");
                setPreview(null);
                setResult(null);
              }}
            >
              Torna al catalogo
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
